import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, memo } from "react";
import { motion } from "framer-motion";
import { RiImageAddFill } from "react-icons/ri";
import axios from "../../../config/axios";
import LoadingModal from "../../../components/loading-modal";

// 🔹 Hook personnalisé pour gérer les images
function useProjectImages(idProjet) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const fetchImages = useCallback(async () => {
    if (!idProjet) return;
    try {
      setLoading(true);
      const { data } = await axios.get(`/cfp/images/${idProjet}`);
      setImages(data?.images || []);
    } catch (err) {
      console.error("Erreur lors du chargement :", err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [idProjet]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages, refresh]);

  return {
    images,
    setImages,
    loading,
    refreshImages: () => setRefresh((r) => r + 1),
  };
}

// 🔹 Hook pour gérer les uploads
function useImageUploader(idProjet, onSuccess) {
  const [preview, setPreview] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Format non supporté (image uniquement)");
      return;
    }
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const upload = async () => {
    if (!photo) return alert("Veuillez sélectionner une image");

    const reader = new FileReader();
    reader.readAsDataURL(photo);

    reader.onloadend = async () => {
      const base64Photo = reader.result;

      try {
        setLoading(true);
        await axios.post(
          `/cfp/images/${idProjet}`,
          {
            description,
            photo: base64Photo,
            idProjet,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );

        // 🔹 Afficher la réponse Laravel dans la consol

        setDescription("");
        setPreview(null);
        setPhoto(null);
        onSuccess?.();
      } catch (err) {
        console.error("Erreur upload :", err.response?.data || err.message);
        alert("Erreur lors du téléchargement");
      } finally {
        setLoading(false);
      }
    };
  };

  return {
    preview,
    photo,
    description,
    setDescription,
    handleFileChange,
    upload,
    loading,
  };
}
// 🔹 Composant principal
export function CrudImageCfp() {
  const { state } = useLocation();
  const idProjet = state?.idProjet;
  const { project_titre, start_date, end_date } = state || {};

  const { images, setImages, refreshImages, loading } =
    useProjectImages(idProjet);

  return (
    <main className="px-4 py-6 mx-auto space-y-8 max-w-7xl sm:px-6 lg:px-8">
      <ProjectGallery
        nomProjet={project_titre}
        minDate={start_date}
        maxDate={end_date}
        idProjet={idProjet}
        images={images}
        setImages={setImages}
        refreshImages={refreshImages}
        loading={loading}
      />
    </main>
  );
}

// 🔹 Galerie principale (avec design de l'ancien code)
const ProjectGallery = memo(
  ({
    nomProjet,
    minDate,
    maxDate,
    idProjet,
    images,
    setImages,
    refreshImages,
    loading,
  }) => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [fullscreen, setFullscreen] = useState(null);
    const [editing, setEditing] = useState(null);
    const [editDescription, setEditDescription] = useState("");

    const uploader = useImageUploader(idProjet, () => {
      refreshImages();
      setShowModal(false);
    });

    const deleteImage = async (id) => {
      if (!window.confirm("Supprimer cette image ?")) return;
      try {
        await axios.delete(`/cfp/images/${id}`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        });
        setImages((prev) => prev.filter((img) => img.idImages !== id));
      } catch (err) {
        alert("Erreur lors de la suppression");
      }
    };

    const saveDescription = async (id) => {
      try {
        await axios.put(
          `/cfp/images/${id}`,
          { description: editDescription },
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`,
            },
          }
        );
        setImages((prev) =>
          prev.map((img) =>
            img.idImages === id
              ? { ...img, img_description: editDescription }
              : img
          )
        );
        setEditing(null);
      } catch (err) {
        alert("Erreur lors de la mise à jour");
      }
    };

    const downloadImage = async (url) => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = url.split("/").pop();
        link.click();
        URL.revokeObjectURL(link.href);
      } catch {
        alert("Erreur lors du téléchargement");
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col gap-4"
      >
        {/* HEADER avec icônes SVG */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex items-center justify-between w-full"
        >
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <svg
                className="w-6 h-6 lucide lucide-chevron-left"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
              >
                <path d="m15 18-6-6 6-6"></path>
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{nomProjet}</h1>
              <div className="flex items-center mt-2 text-sm text-gray-500">
                <svg
                  className="w-4 h-4 mr-1 lucide lucide-calendar"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect width="18" height="18" x="3" y="4" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                <span>
                  {minDate} - {maxDate}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 lucide lucide-plus"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5v14"></path>
            </svg>
            Ajouter des photos
          </button>
        </motion.div>

        {/* MODAL UPLOAD avec design de l'ancien code */}
        {showModal && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="p-6 bg-white rounded-lg shadow-sm"
          >
            <h3 className="mb-4 text-lg font-semibold">Ajouter une photo</h3>
            <div className="space-y-4">
              <div className="p-8 border-2 border-gray-300 border-dashed rounded-lg">
                <div className="flex flex-col items-center">
                  {!uploader.preview ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-12 h-12 mb-3 text-gray-400 lucide lucide-upload"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" x2="12" y1="3" y2="15"></line>
                    </svg>
                  ) : (
                    <img
                      src={uploader.preview}
                      alt="Aperçu"
                      className="w-20 mb-3 rounded-md shadow-md"
                    />
                  )}
                  <label className="px-4 py-2 text-white transition-colors bg-blue-500 rounded-md cursor-pointer hover:bg-blue-600">
                    Sélectionner une photo
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={uploader.handleFileChange}
                    />
                  </label>
                  <p className="mt-2 text-sm text-gray-500">
                    JPG, PNG, GIF jusqu'à 10MB
                  </p>
                </div>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows="3"
                  placeholder="Décrivez cette photo..."
                  value={uploader.description}
                  onChange={(e) => uploader.setDescription(e.target.value)}
                />
              </div>
              <button
                onClick={uploader.upload}
                disabled={!uploader.photo}
                className={`w-full px-4 py-2 font-medium text-white rounded-md ${
                  uploader.photo
                    ? "bg-blue-500 hover:bg-blue-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Télécharger la photo
              </button>
              <LoadingModal
                show={uploader.loading}
                message="Téléchargement en cours..."
              />
            </div>
          </motion.div>
        )}

        {/* GALERIE */}
        {loading ? (
          <p className="text-center text-gray-500">Chargement...</p>
        ) : images.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid gap-4 justify-center grid-cols-[repeat(auto-fill,minmax(200px,1fr))]"
          >
            {images.map((img, index) => (
              <motion.div
                key={img.idImages}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <ImageCard
                  image={img}
                  editing={editing}
                  editDescription={editDescription}
                  setEditDescription={setEditDescription}
                  setEditing={setEditing}
                  saveDescription={saveDescription}
                  deleteImage={deleteImage}
                  downloadImage={downloadImage}
                  setFullscreen={setFullscreen}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center text-gray-500">Aucune image</div>
        )}

        {/* FULLSCREEN */}
        {fullscreen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
            onClick={() => setFullscreen(null)}
          >
            <span
              className="absolute text-4xl text-white cursor-pointer top-4 right-4"
              onClick={() => setFullscreen(null)}
            >
              &times;
            </span>
            <img
              src={fullscreen}
              alt="Plein Écran"
              className="max-w-full max-h-full"
            />
          </div>
        )}
      </motion.div>
    );
  }
);

// 🔹 Carte image individuelle (avec icônes SVG de l'ancien code)
const ImageCard = memo(
  ({
    image,
    editing,
    editDescription,
    setEditDescription,
    setEditing,
    saveDescription,
    deleteImage,
    downloadImage,
    setFullscreen,
  }) => {
    const isEditing = editing === image.idImages;

    return (
      <div className="relative group">
        <div className="relative inline-block overflow-hidden bg-gray-100 rounded-lg cursor-pointer">
          <img
            src={`${process.env.REACT_APP_URL_IMAGE}/${image.img_url}`}
            alt={image.img_description}
            onClick={() => setFullscreen(image.img_url)}
            className="object-cover w-full h-full"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="absolute flex gap-2 transition-opacity opacity-5 top-2 right-2 group-hover:opacity-100">
          {image.is_added && (
            <button
              onClick={() => {
                setEditing(image.idImages);
                setEditDescription(image.img_description || "");
              }}
              className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              title="Modifier"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4 text-gray-600 lucide lucide-pen"
              >
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path>
              </svg>
            </button>
          )}
          <button
            onClick={() => downloadImage(image.img_url)}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
            title="Télécharger"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-4 h-4 text-gray-600 lucide lucide-download"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" x2="12" y1="15" y2="3"></line>
            </svg>
          </button>
          {image.is_added && (
            <button
              onClick={() => deleteImage(image.idImages)}
              className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              title="Supprimer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-4 h-4 text-red-600 lucide lucide-trash2"
              >
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                <line x1="10" x2="10" y1="11" y2="17"></line>
                <line x1="14" x2="14" y1="11" y2="17"></line>
              </svg>
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="mt-2">
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="2"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-1">
              <button
                onClick={() => setEditing(null)}
                className="px-3 py-1 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={() => saveDescription(image.idImages)}
                className="px-3 py-1 text-white bg-blue-500 rounded-md hover:bg-blue-600"
              >
                Enregistrer
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-sm text-gray-600">
            {image.img_description || "Aucune description"}
          </p>
        )}
        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
          <RiImageAddFill />
          <span>
            <span className="font-medium">Ajoute par</span>{" "}
            {image?.role_name_adder || "inconnu"}
          </span>
        </div>
      </div>
    );
  }
);
